import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  InputGroup,
  HStack,
  VStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  FormLabel,
  FormControl,
} from "@chakra-ui/react";
import { FaPlus, FaCalendarAlt, FaClock, FaEye, FaEdit, FaTimes, FaFileExcel, FaCheck } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getAppointments, cancelAppointment, createAppointment, updateAppointment, getAppointmentById,scheduledAppointment } from '../services/appointmentService';
import toast from 'react-hot-toast';
import CustomModal from '../components/Modal';
import MultiStepAppointmentForm from '../components/forms/MultiStepAppointmentForm';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import JalaliDatePicker from "../components/JalaliDatePicker";
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { exportAppointmentsToExcel } from '../utils/excelExport';
import Pagination from '../components/Pagination';
import AppointmentForm from "../components/forms/AppointmentForm";
import { jalaliToGregorian as jalaliToGregorianUtil } from '../utils/timeUtils';
import { getConsultants } from "../services/consultantService";
import SearchableSelect from "../components/SearchableSelect";

function Appointments() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const {  isAdmin, isConsultant, isManager } = useAuth();
  const navigate = useNavigate();
  
  // State برای فیلترها و modal
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ConsultantFilter,setConsultantFilter]=useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const formRef = useRef(null);
  
  // React Query hooks
  const queryClient = useQueryClient();
  
  // دریافت لیست ملاقات‌ها
  const {
    data: appointmentsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['appointments', currentPage, itemsPerPage, searchTerm, statusFilter,ConsultantFilter, dateFilter],
    () => {
      const params = { 
        page: currentPage, 
        limit: itemsPerPage,
        search: searchTerm,
        status: statusFilter ,
        consultant:ConsultantFilter
      };

      if (dateFilter) {
        const gDate = jalaliToGregorianUtil(dateFilter);
        if (gDate) {
          params.date = gDate.toISOString().split('T')[0];
        }
      }

      return getAppointments(params);
    },
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  );

  
  const { data: consultantsData } = useQuery(['consultants'], () => getConsultants({ limit: 100 }), {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });
    console.log('consultantsData',consultantsData)
const consultants = [
  { name: 'همه ', _id: '' },
  ...(Array.isArray(consultantsData?.data?.consultants) ? consultantsData?.data?.consultants : [])
];
  // Mutation برای لغو ملاقات
  const cancelAppointmentMutation = useMutation(cancelAppointment, {
    onSuccess: () => {
      toast.success('ملاقات با موفقیت لغو شد');
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['consultants']); // invalidate cache مشاوران
      queryClient.invalidateQueries(['clients']); // invalidate cache مراجعان
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در لغو ملاقات');
    }
  });

  const scheduledAppointmentMutation = useMutation(scheduledAppointment, {
    onSuccess: () => {
      toast.success('ملاقات با موفقیت تایید شد');
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['consultants']); // invalidate cache مشاوران
      queryClient.invalidateQueries(['clients']); // invalidate cache مراجعان
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در لغو ملاقات');
    }
  });

  // Mutation برای ایجاد ملاقات
  const createAppointmentMutation = useMutation(createAppointment, {
    onSuccess: () => {
      toast.success('ملاقات با موفقیت رزرو شد');
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['consultants']); // invalidate cache مشاوران
      queryClient.invalidateQueries(['clients']); // invalidate cache مراجعان
      setIsModalOpen(false);
      setEditingAppointment(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در رزرو ملاقات');
    }
  });

  // Mutation برای بروزرسانی ملاقات
  const updateAppointmentMutation = useMutation(updateAppointment, {
    onSuccess: () => {
      toast.success('ملاقات با موفقیت بروزرسانی شد');
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['consultants']); // invalidate cache مشاوران
      queryClient.invalidateQueries(['clients']); // invalidate cache مراجعان
      setIsModalOpen(false);
      setEditingAppointment(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در بروزرسانی ملاقات');
    }
  });

  const handleScheduledAppointment = (appointmentId) => {
    if (window.confirm('آیا از تایید این ملاقات اطمینان دارید؟')) {
      scheduledAppointmentMutation.mutate(appointmentId);
    }
  };

  // تابع لغو ملاقات
  const handleCancelAppointment = (appointmentId) => {
    if (window.confirm('آیا از لغو این ملاقات اطمینان دارید؟')) {
      cancelAppointmentMutation.mutate(appointmentId);
    }
  };

  // تابع باز کردن modal برای افزودن
  const handleAddAppointment = () => {
    setIsEditModalOpen(false);
    setEditingAppointment(null);
    setIsModalOpen(true);
  };

  // تابع مشاهده جزئیات ملاقات
  const handleViewDetails = (appointmentId) => {
    navigate(`/appointment/${appointmentId}`);
  };

  // تابع باز کردن modal برای ویرایش
  const handleEditAppointment = async (appointment) => {
    try {
      const fullAppointmentData = await getAppointmentById(appointment._id || appointment.id);
      const fullAppointment = fullAppointmentData.data.appointment;
      
      // تمیز کردن داده‌ها برای جلوگیری از circular reference
      const cleanAppointment = {
        _id: fullAppointment._id,
        id: fullAppointment.id,
        clientId: fullAppointment.clientId || fullAppointment.client?._id,
        consultantId: fullAppointment.consultantId || fullAppointment.consultant?._id,
        client: fullAppointment.client, // اضافه کردن اطلاعات کامل مراجع
        consultant: fullAppointment.consultant, // اضافه کردن اطلاعات کامل مشاور
        date: fullAppointment.date || '',
        time: fullAppointment.time || '',
        duration: fullAppointment.duration || 60,
        status: fullAppointment.status || 'scheduled',
        paymentStatus: fullAppointment.paymentStatus || 'pending',
        meetingType: fullAppointment.meetingType || 'in-person',
        notes: fullAppointment.notes || '',
        consultantNotes: fullAppointment.consultantNotes || '',
        price: fullAppointment.cost || fullAppointment.price || 0
      };
      
      setEditingAppointment(cleanAppointment);
      setIsEditModalOpen(true);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      toast.error('خطا در دریافت اطلاعات ملاقات');
    }
  };

  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAppointment(null);
  };

  // توابع مدیریت pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // بازگشت به صفحه اول
  };

  // تابع ارسال فرم
  const handleSubmitForm = (data) => {
    console.log('Form data received:', data);
    
    let cleanData;
    
    if (isConsultant()) {
      // مشاوران فقط می‌توانند سه فیلد را تغییر دهند
      cleanData = {
        meetingType: data.meetingType || 'in-person',
        status: data.status || 'scheduled',
        consultantNotes: data.consultantNotes || '',
      };
    } else {
      // مدیران می‌توانند همه فیلدها را تغییر دهند
      cleanData = {
        clientId: data.clientId,
        consultantId: data.consultantId,
        date: data.date ? new Date(data.date).toISOString() : data.date,
        time: data.time,
        duration: data.duration || 60,
        status: data.status || 'scheduled',
        paymentStatus: data.paymentStatus || 'pending',
        meetingType: data.meetingType || 'in-person',
        notes: data.notes || '',
        consultantNotes: data.consultantNotes || '',
        price: data.price || 0
      };
    }
    
   
    
    if (editingAppointment) {
      // ویرایش ملاقات موجود
      updateAppointmentMutation.mutate({ 
        appointmentId: editingAppointment._id || editingAppointment.id, 
        appointmentData: cleanData,
        isAdmin: isAdmin()
      });
    } else {
      // افزودن ملاقات جدید
      createAppointmentMutation.mutate(cleanData);
    }
  };

  // تابع جستجو
  

  // تابع فیلتر بر اساس وضعیت
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };
  const handleConsultantFilter = (value) => {
    setConsultantFilter(value);
    setCurrentPage(1);
  };

  // تابع export به اکسل
  const handleExportToExcel = () => {
    try {
      const appointments = appointmentsData?.data?.appointments || appointmentsData?.appointments || appointmentsData?.data || appointmentsData || [];
      exportAppointmentsToExcel(appointments);
      toast.success('فایل اکسل با موفقیت دانلود شد');
    } catch (error) {
      console.error('خطا در export اکسل:', error);
      toast.error('خطا در دانلود فایل اکسل');
    }
  };

  // تابع تبدیل وضعیت به رنگ
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'cancelled':
        return 'red';
      case 'completed':
        return 'blue';
      case 'no-show':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // تابع تبدیل وضعیت به فارسی
  const getStatusText = (status) => {
    switch (status) {
      case 'scheduled':
        return 'تأیید شده';
      case 'pending':
        return 'در انتظار';
      case 'cancelled':
        return 'لغو شده';
      case 'completed':
        return 'انجام شده';
      case 'no-show':
        return 'حضور نیافت';
      default:
        return 'نامشخص';
    }
  };

  // تابع تبدیل وضعیت پرداخت به رنگ
  const getPaymentColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'partial':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // تابع تبدیل وضعیت پرداخت به فارسی
  const getPaymentText = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return 'پرداخت شده';
      case 'pending':
        return 'در انتظار';
      case 'partial':
        return 'نیمه پرداخت';
      default:
        return 'نامشخص';
    }
  };

  // تابع تبدیل نوع ملاقات به فارسی
  const getMeetingTypeText = (meetingType) => {
    switch (meetingType) {
      case 'in-person':
        return 'حضوری';
      case 'phone':
        return 'تلفنی';
      case 'online':
        return 'آنلاین';
      default:
        return 'نامشخص';
    }
  };

  // تابع تبدیل نوع ملاقات به رنگ
  const getMeetingTypeColor = (meetingType) => {
    switch (meetingType) {
      case 'in-person':
        return 'blue';
      case 'phone':
        return 'green';
      case 'online':
        return 'purple';
      default:
        return 'gray';
    }
  };

  // تابع نمایش یادداشت‌ها بر اساس نقش کاربر
  const getNotesDisplay = (appointment) => {
    if (isConsultant()) {
      // مشاور فقط یادداشت‌های خودش را می‌بیند
      return appointment.consultantNotes || 'یادداشتی ثبت نشده';
    } else if (isAdmin()) {
      // مدیر یادداشت‌های عمومی را می‌بیند
      return appointment.notes || 'یادداشتی ثبت نشده';
    }
    return 'یادداشتی ثبت نشده';
  };

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری ملاقات‌ها...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "20px", md: "7px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  // داده‌های ملاقات‌ها
  const appointments = appointmentsData?.data?.appointments || appointmentsData?.appointments || appointmentsData?.data || appointmentsData || [];

  return (
    <>
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Box p="20px 0px 35px 0px">
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  مدیریت ملاقات‌ها
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهده و مدیریت ملاقات‌های کلینیک
                </Text>
              </Flex>
            </Box>
            <Box px="0px" pt="0px" pb="25px">
              <Flex px="25px" mb="25px" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
                <Flex 
                  gap="15px" 
                  alignItems="center" 
                  flexDirection="row"
                  flexWrap="nowrap"  // مهم! nowrap کنار هم نگه می‌دارد
                >
                  <InputGroup maxW="220px">
                    <JalaliDatePicker
                      value={dateFilter}
                      onChange={(value) => {
                        setDateFilter(value || '');
                        setCurrentPage(1);
                      }}
                      placeholder="تاریخ را انتخاب کنید"
                      saveAsJalali={true}
                      label="تاریخ ملاقات"
                    />
                  </InputGroup>
                  {dateFilter && (
                    <Button
                      size="md"
                      variant="outline"
                      borderRadius="15px"
                      fontSize="xs"
                      marginTop="20px"
                      onClick={() => {
                        setDateFilter('');
                        setCurrentPage(1);
                      }}
                    >
                      حذف تاریخ 
                    </Button>
                  )}
                  
                  <FormControl maxW="200px">
                    <FormLabel fontSize="sm" mb="10px">
                      فیلتر وضعیت
                    </FormLabel>
                    <Select
                      borderRadius="5px"
                      fontSize="sm"
                      value={statusFilter}
                      onChange={(e) => handleStatusFilter(e.target.value)}
                    >
                      <option value="">همه</option>
                      <option value="scheduled">تأیید شده</option>
                      <option value="pending">در انتظار</option>
                      <option value="cancelled">لغو شده</option>
                      <option value="completed">انجام شده</option>
                      <option value="no-show">حضور نیافت</option>
                    </Select>
                  </FormControl>
                  <FormControl maxW="200px">
                    <FormLabel fontSize="sm" mb="10px">
                       انتخاب مشاور
                    </FormLabel>
                    <SearchableSelect
          options={consultants}
          value={ConsultantFilter}
          
          onChange={(value) => {
           handleConsultantFilter(value)
          }}
          placeholder="مشاور را انتخاب کنید"
          searchPlaceholder="جستجو در مشاورین..."
          getOptionLabel={(client) => `${client.name} `}
          getOptionValue={(client) => client._id}

        />
                  </FormControl>
                  
                  
                </Flex>
                <HStack spacing={3}>
                  {(isAdmin() || isManager()) && (
                    <Button
                      leftIcon={<FaFileExcel />}
                      colorScheme="green"
                      borderRadius="15px"
                      fontSize="sm"
                      fontWeight="bold"
                      px="20px"
                      py="10px"
                      onClick={handleExportToExcel}
                    >
                      خروجی اکسل
                    </Button>
                  )}
                  {isAdmin() && (
                    <Button
                      leftIcon={<FaPlus />}
                      colorScheme="teal"
                      borderRadius="15px"
                      fontSize="sm"
                      fontWeight="bold"
                      px="20px"
                      py="10px"
                      onClick={handleAddAppointment}
                    >
                      رزرو ملاقات جدید
                    </Button>
                  )}
                </HStack>
              </Flex>
              
              {/* جدول ملاقات‌ها */}
              <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                <Table variant="simple" color={textColor} size="sm">
                  <Thead>
                    <Tr my=".8rem" pl="0px" color="gray.400">
                      <Th borderColor={borderColor} textAlign="right">
                        مراجع
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        شماره پرونده
                      </Th>
                      {!isConsultant() && (
                        <Th borderColor={borderColor} textAlign="right">
                          مشاور
                        </Th>
                      )}
                      <Th borderColor={borderColor} textAlign="right">
                        تاریخ و زمان
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        مدت
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        نوع ملاقات
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                         تاریخ ثبت 
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        وضعیت
                      </Th>
                      {!isConsultant() && (
                        <Th borderColor={borderColor} textAlign="right">
                          پرداخت
                        </Th>
                      )}
                      <Th borderColor={borderColor} textAlign="right">
                        یادداشت‌ها
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        عملیات
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {appointments.length === 0 ? (
                      <Tr>
                        <Td colSpan={isConsultant() ? 8 : 10} textAlign="center" py={8}>
                          <Text color="gray.500">هیچ ملاقات‌ای یافت نشد</Text>
                        </Td>
                      </Tr>
                    ) : (
                      appointments.map((appointment) => (
                        <Tr key={appointment._id || appointment.id}>
                          <Td>
                            <Flex direction="column">
                              <Text fontSize="md" color={textColor} fontWeight="bold">
                                {appointment.client?.firstName && appointment.client?.lastName
                                  ? `${appointment.client.firstName} ${appointment.client.lastName}`
                                  : appointment.clientName || 'نامشخص'
                                }
                              </Text>
                              <Text fontSize="sm" color="gray.400">
                                {appointment.client?.nationalId || 'کد ملی ثبت نشده'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {appointment.fileNumber || appointment.client?.fileNumber || 'شماره پرونده ثبت نشده'}
                            </Text>
                          </Td>
                          {!isConsultant() && (
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="md" color={textColor} fontWeight="bold">
                                  {appointment.consultant?.firstName && appointment.consultant?.lastName 
                                    ? `${appointment.consultant.firstName} ${appointment.consultant.lastName}`
                                    : appointment.consultant?.name || appointment.consultantName || 'نامشخص'
                                  }
                                </Text>
                                {appointment.consultant?.email && (
                                  <Text fontSize="sm" color="gray.400">
                                    {appointment.consultant.email}
                                  </Text>
                                )}
                                {appointment.consultant?.phone && (
                                  <Text fontSize="sm" color="gray.400">
                                    {appointment.consultant.phone}
                                  </Text>
                                )}
                              </VStack>
                            </Td>
                          )}
                          <Td>
                            <Flex direction="column" alignItems="flex-start">
                              <HStack spacing={2} mb={1}>
                                <FaCalendarAlt color="gray.400" />
                                <JalaliDateDisplay
                                  date={appointment.date}
                                  format="jYYYY/jMM/jDD"
                                  textProps={{
                                    fontSize: "sm",
                                    color: textColor
                                  }}
                                />
                              </HStack>
                              <HStack spacing={2}>
                                <FaClock color="gray.400" />
                                <Text fontSize="sm" color={textColor}>
                                  {appointment.time || 'ساعت ثبت نشده'}
                                </Text>
                              </HStack>
                            </Flex>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {appointment.duration || '0'} دقیقه
                            </Text>
                          </Td>
                          <Td>
                            <Badge
                              bg={`${getMeetingTypeColor(appointment.meetingType)}.400`}
                              color="white"
                              fontSize="16px"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {getMeetingTypeText(appointment.meetingType)}
                            </Badge>
                          </Td>
                          <Td>
                              <HStack spacing={2} mb={1}>
                                <FaCalendarAlt color="gray.400" />
                                <JalaliDateDisplay
                                  date={appointment.createdAt}
                                  format="jYYYY/jMM/jDD"
                                  textProps={{
                                    fontSize: "sm",
                                    color: textColor
                                  }}
                                />
                              </HStack>
                              <HStack spacing={2}>
                                <FaClock color="gray.400" />
                                <Text fontSize="sm" color={textColor}>
                                  {appointment.createdAt?.split('T')[1]?.split(':')[0]+':'+appointment.createdAt?.split('T')[1]?.split(':')[1] || 'ساعت ثبت نشده'}
                                </Text>
                              </HStack>
                          </Td>
                          <Td>
                            <Badge
                              bg={`${getStatusColor(appointment.status)}.400`}
                              color="white"
                              fontSize="16px"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {getStatusText(appointment.status)}
                            </Badge>
                          </Td>
                          {!isConsultant() && (
                            <Td>
                              <Badge
                                bg={`${getPaymentColor(appointment.paymentStatus)}.400`}
                                color="white"
                                fontSize="16px"
                                p="3px 10px"
                                borderRadius="8px"
                              >
                                {getPaymentText(appointment.paymentStatus)}
                              </Badge>
                            </Td>
                          )}
                          <Td>
                            <Text fontSize="sm" color={textColor} maxW="200px" noOfLines={2}>
                              {getNotesDisplay(appointment)}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Button
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                leftIcon={<FaEye />}
                                onClick={() => handleViewDetails(appointment._id || appointment.id)}
                              >
                                جزئیات
                              </Button>
                              {(isAdmin() || isConsultant()) && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                  leftIcon={<FaEdit />}
                                  onClick={() => handleEditAppointment(appointment)}
                                >
                                  ویرایش
                                </Button>
                              )}
                              {isAdmin() && appointment.status !== 'cancelled' && appointment.status !== 'no-show' && (
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  leftIcon={<FaTimes />}
                                  onClick={() => handleCancelAppointment(appointment._id || appointment.id)}
                                  isLoading={cancelAppointmentMutation.isLoading}
                                >
                                  لغو
                                </Button>
                              )}
                              {isAdmin() && appointment.status !== 'scheduled' && appointment.status !== 'no-show' && (
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  variant="outline"
                                  leftIcon={<FaCheck />}
                                  onClick={() => handleScheduledAppointment(appointment._id || appointment.id)}
                                  isLoading={scheduledAppointmentMutation.isLoading}
                                >
                                  تایید
                                </Button>
                              )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {appointmentsData?.data?.pagination && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={appointmentsData.data.pagination.totalPages}
                  totalItems={appointmentsData.data.pagination.totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal فرم ملاقات */}
      {isEditModalOpen ? (
        <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={'ویرایش ملاقات' }
        size="xl"
        hideFooter={false}
        onSubmit={() => {
          formRef.current?.submit();
        }}
      >
      
        <AppointmentForm
          ref={formRef}
          appointment={editingAppointment}
          onSubmit={handleSubmitForm}
          isLoading={createAppointmentMutation.isLoading || updateAppointmentMutation.isLoading}
        />
        </CustomModal>
        ) : (
          <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={ 'رزرو ملاقات جدید'}
        size="xl"
        hideFooter={true}
      >
        <MultiStepAppointmentForm
          ref={formRef}
          appointment={editingAppointment}
          onSubmit={handleSubmitForm}
          isLoading={createAppointmentMutation.isLoading || updateAppointmentMutation.isLoading}
        />
        </CustomModal>
        )}
     
    </>
  );
}

export default Appointments; 